import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Location} from '../../../../shared/types/location.type';
import {ProjectMeasureGraphicStateService} from '../../../../shared/services/project-measure-graphic-state/project-measure-graphic-state.service';
import {HelperService as HelpersService} from '../../../../shared/services/helper/helper.service';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {
  faCheck,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import unit = Location.unit;
import {DragDropLocationsModalComponent} from '../../../../shared/components/drag-drop-locations-modal/drag-drop-locations-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {faSort} from '@fortawesome/free-solid-svg-icons/faSort';
import {DragDropAlertModalComponent} from '../../../../shared/components/drag-drop-locations-modal/drag-drop-alert-modal/drag-drop-alert-modal.component';

type ILocation = Location.clientSide;

@Component({
  selector: 'app-location-tree',
  templateUrl: './location-tree.component.html',
  styleUrls: ['./location-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationTreeComponent implements OnInit, AfterViewInit, OnChanges {

  faSort = faSort;
  locations: ILocation[];
  allSelectedValue = true;
  faCheck = faCheck;
  faSync = faSync;
  showDragDropButton = true;

  @Input() height: number;
  @ViewChild('container') container: ElementRef;


  constructor(private state: ProjectMeasureGraphicStateService,
              private changeDetectorRef: ChangeDetectorRef,
              private matDialog: MatDialog) {
  }

  ngOnInit() {
    this.state.locationsInitedOrChanged$.subscribe(
      (locations: ILocation[]) => {
        this.locations = locations;
        this.changeDetectorRef.detectChanges();
      }
    );
    this.state.unitToogled$.subscribe(
      ([unit, visible]) => {
        this.setTreeSelectedByUnit(unit, visible);
        this.changeDetectorRef.detectChanges();
      }
    );
  }

  ngAfterViewInit() {
    this.updateHeight();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateHeight();
  }

  selectAllLocations() {
    const names: string[] = [];
    const selectUnit = (locationId) => (unit) => {
        unit.selected = this.allSelectedValue;
        names.push(locationId + '_' + unit.unit);
      };

    this.locations.forEach((location) => {
      location.selected = this.allSelectedValue;
      if (location.unit) {
        location.unit.forEach(selectUnit(location.id));
      }
      if (location.child) {
        location.child.forEach(
            (child) => {
              child.selected = this.allSelectedValue;
              if (child.unit) {
                child.unit.forEach(selectUnit(child.id));
              }
            }
        )
      }
    });
    this.updateLocationsInState(this.allSelectedValue, names, () => this.allSelectedValue = !this.allSelectedValue)
  }

  selectUnit(unit: unit, location: Location.clientSide, event: MatCheckboxChange) {
    const checked = event.checked;
    const name = location.id + '_' + unit.unit;
    unit.selected = checked;
    this.updateLocationCheckedStateAccordingToChildren(checked, location);
    this.updateLocationsInState(checked, [name]);
  }

  selectLocation(location: Location.clientSide, event: MatCheckboxChange, parent: Location.clientSide = null) {
    const checked = event.checked;

    let name: string[] = [];
    location.selected = checked;

    if (location.unit) {
      if (location.unit) {
        location.unit.forEach((u) => {
          u.selected = checked;
          name.push(location.id + '_' + u.unit);
        });
      }
    }

    if (location.child) {
      location.child.forEach((child) => {
        child.selected = checked;
        if (child.unit) {
          child.unit.forEach((u) => {
            u.selected = checked;
            name.push(child.id + '_' + u.unit);
          });
        }
      });
    }

    if (parent) {
      this.updateLocationCheckedStateAccordingToChildren(checked, parent);
    }
    this.updateLocationsInState(checked, name);
  }

  openDragDropLocationsList() {
    this.showDragDropButton = false;
    this.matDialog.open(DragDropLocationsModalComponent, {
      height: '90%',
      width: '800px',
      panelClass: 'drag-drop-modal'
    }).afterClosed().subscribe(
      () => {
        this.showDragDropButton = true;
        this.locations = this.state.get('locations');
        this.changeDetectorRef.detectChanges();
      }
    );
    this.matDialog.open(DragDropAlertModalComponent, {
      height: '23%',
      width: '45%'
    });
  }

  setTreeLocationIndeterminate(location) {
    if (location.unit && location.unit.length > 0) {
      let indeterminate = 0;
      location.unit.forEach((child) => {
        if (child.selected === true) {
          indeterminate++;
        }
      });
      if (indeterminate === 0) {
        return false;
      }
      if (indeterminate < location.unit.length) {
        return true;
      }
    } else if (location.child && location.child.length > 0) {
      let indeterminate = 0;
      let unitCount = 0;
      location.child.forEach((child) => {
        if (child.selected === true) {
          indeterminate++;
        }
        if (child.unit && child.unit.length > 0) {
          child.unit.forEach((unit) => {
            unitCount++;
            if (unit.selected === true) {
              indeterminate++;
            }
          });
        }
      });
      if (indeterminate === 0) {
        return false;
      }
      if (indeterminate < (location.child.length + unitCount)) {
        return true;
      }
    }
  }

  getImg(type: any) {
    return HelpersService.imageByType(type);
  }

  panelToggled(location: ILocation, expanded: boolean) {
    location.expanded = expanded;
    this.state.updateState({locations: this.locations});
  }

  loadChart(reset = false) {
    this.state.loadGraph$.next(reset);
    this.state.persistViewAfterLocationsChange$.next();
  }

  private setTreeSelectedByUnit(unit: string, selected: boolean) {
    this.locations.forEach((loc) => {
      if (loc.unit.length > 0) {
        loc.unit.forEach(
          u => {
            if (u.unit === unit && loc.selected) {
              u.selected = selected;
            }
          });
      }
      if (loc.child.length > 0) {
        loc.child.forEach((child) => {
          if (child.unit.length > 0) {
            child.unit.forEach(
              cu => {
                if (cu.unit === unit && child.selected) {
                  cu.selected = selected;
                }
              });
          }
        });
      }
    });
  }

  private updateHeight() {
    // could not use ngStyle directly because, in some cases, the initial height value assignation is not detected, so we need
    // to manually set it in ngAfterViewInit, in addition to setting it on detection changes
    if (this.container) {
      this.container.nativeElement.style.height = this.height - 30 + 'px';
    }
  }

  private updateLocationsInState(toSelect: boolean, names: string[], callback?: Function) {
    this.changeDetectorRef.detectChanges();
    setTimeout(() => {
      this.state.setLocationSelected(this.locations, toSelect, names);
      if (callback) {
        callback();
      }
    }, 0);
  }

  private updateLocationCheckedStateAccordingToChildren(checked: boolean, location: any) {
    if (checked) {
      const isCompletelyChecked = -1 === location.unit.findIndex((l) => !l.selected);
      if (isCompletelyChecked) {
        location.selected = true;
      }
    } else {
      const isCompletelyUnchecked = -1 === location.unit.findIndex((l) => l.selected);
      if (isCompletelyUnchecked) {
        location.selected = false;
      }
    }
  }

}
